<script setup>
import { i18n } from 'boot/i18n';
import {
  HrList,
  HrListAddBtn,
  HrListItem,
  HrListSectionHeader,
  HrOverflowMenuBtn,
  HrStatusActionChip,
  useDialog,
} from 'src/design-system';
import { computed, ref, shallowRef, toRef } from 'vue';

import { useStaffQualifications } from '../composables/useStaffQualifications';
import { degreeCaption, splitCredentials } from '../presenters/qualificationRows';
import CredentialList from './CredentialList.vue';
import EditCredentialDialog from './EditCredentialDialog.vue';
import EditDegreeDialog from './EditDegreeDialog.vue';

/**
 * What a staff record says this person is qualified to do, and from when.
 *
 * IT WRITES NOTHING, the arrangement `ProviderPanel` already uses on this same screen and for the
 * same reason: these lists live on the document the staff editor is saving, so a write from here
 * would be a second writer and the host's own save would put its stale copy back over the top. The
 * host passes the record it is editing and merges the patch it gets back, and never sees a Degree or
 * a Credential.
 *
 * TWO GROUPS FOR CREDENTIALS, CURRENT AND PAST, following `ClientCoveragePanel`. The past group
 * renders only when something is in it, so a clinic that has never had one never meets an empty
 * heading. Membership is derived from the dates, so a credential arrives in the past group on its
 * own as reality moves.
 *
 * TODAY IS READ HERE AND NOWHERE DEEPER. This is the edge that owns the clock: the presenter and the
 * domain take the date as an argument, so nothing below this file can quietly answer today's
 * question for a record being read about some other day.
 */
defineOptions({ name: 'QualificationsPanel' });

const props = defineProps({
  /** The stored person record the host is editing, in the platform's own shape. */
  person: {
    type: Object,
    default: null,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits({
  /** A patch to merge into the host's draft: `{ degrees, credentials }`. */
  change: null,
});

/**
 * Today, read once, here, as a LOCAL calendar day.
 *
 * Not `toISOString()`, which is the UTC day: for a clinic west of UTC that is already tomorrow by
 * the evening, and a credential expiring today would be filed under Expired and collapsed out of
 * sight while it is still valid. The stored dates are calendar dates, so the comparison has to be
 * made in the reader's own calendar.
 *
 * This file is the edge that owns the clock. The presenter and the domain take the date as an
 * argument, so nothing below can quietly answer today's question about some other day.
 */
const asOf = (() => {
  const now = new Date();
  const month = `${now.getMonth() + 1}`.padStart(2, '0');

  return `${now.getFullYear()}-${month}-${`${now.getDate()}`.padStart(2, '0')}`;
})();

const { qualifications, patchFor } = useStaffQualifications(toRef(props, 'person'));
const groups = computed(() => splitCredentials(qualifications.value.credentials, asOf));

const degreeRows = computed(() => {
  return qualifications.value.degrees.map((degree, index) => ({
    key: `${degree.level}-${index}`,
    degree,
    label: i18n.t(`staff.qualifications.levels.${degree.level}`),
    caption: degreeCaption(degree),
  }));
});

const publish = (next) => emit('change', patchFor(next));

const { confirmDestructiveDialog } = useDialog();

/**
 * Asked before, like every other delete on this tab.
 *
 * There is nothing to undo once the host saves: the patch replaces the array whole, so the row and
 * the dates a claim resolves through go together. The supervisor and checklist lists beside this one
 * already confirm, and a credential is the one of the three that bills.
 *
 * POSITIONAL, and `confirmLabel` is required — the helper returns without opening anything when
 * `onOk` is missing, so an options object silently makes removal a no-op.
 */
const confirmRemoval = (onOk) => confirmDestructiveDialog(
  i18n.t('common.dialogTitleConfirm'),
  i18n.t('common.notifyDeleteConfirm'),
  onOk,
  { confirmLabel: i18n.t('common.buttonDelete') },
);

function removeDegree(target) {
  confirmRemoval(() => {
    publish({ degrees: qualifications.value.degrees.filter((degree) => degree !== target) });
  });
} // removeDegree()

function removeCredential(target) {
  confirmRemoval(() => {
    publish({ credentials: qualifications.value.credentials.filter((entry) => entry !== target) });
  });
} // removeCredential()

/**
 * Expired credentials are collapsed behind a count rather than listed.
 *
 * The list grows for the whole of somebody's career: a certification renewed every two years leaves
 * a row behind each time, and after ten years the expired ones outnumber the live ones several to
 * one. They are kept because a date of service inside an old window still resolves through them, but
 * almost nobody opening this screen is looking for them, and leaving them open pushes what IS true
 * off the top of the panel.
 *
 * The count sits on the chip so the history reads as THERE rather than missing, which is the
 * difference between collapsing something and hiding it.
 */
const showEnded = ref(false);

const credentialDialog = ref(null);
const degreeDialog = ref(null);
/**
 * shallowRef, NOT ref, and this is load-bearing rather than an optimization.
 *
 * `ref()` deep-wraps an object in `reactive()`, so reading it back hands over a PROXY of what was put
 * in. These lists hold raw objects, so `entry === editing.value` was comparing a proxy against a raw
 * object and never matching: editing a credential found itself missing from the "other records" it
 * is checked against, and reported the certificate as overlapping itself. The same identity is what
 * decides whether a save replaces a row or appends a second one.
 */
const editing = shallowRef(null);

/**
 * Replaces the record being edited, or appends when there was none.
 *
 * Identity here is the OBJECT, not a key: these are entries in a list with no ids of their own, and
 * the entry being edited is the one the row handed over. Matching on a field instead would edit both
 * halves of a renewal, which share a certificate number by design.
 */
const upsert = (list, next) => {
  return editing.value === null
    ? list.concat([next])
    : list.map((entry) => (entry === editing.value ? next : entry));
};

function openCredential(credential = null) {
  editing.value = credential;
  credentialDialog.value?.show({ credential });
} // openCredential()

function openRenewal(credential) {
  editing.value = null;
  credentialDialog.value?.show({ renewing: credential });
} // openRenewal()

function openDegree(degree = null) {
  editing.value = degree;
  degreeDialog.value?.show({ degree });
} // openDegree()

function onCredentialSaved(credential) {
  publish({ credentials: upsert(qualifications.value.credentials, credential) });
} // onCredentialSaved()

function onDegreeSaved(degree) {
  publish({ degrees: upsert(qualifications.value.degrees, degree) });
} // onDegreeSaved()

const degreeMenu = () => [{
  group: 'destructive',
  options: [{
    label: i18n.t('common.buttonDelete'), icon: 'delete', value: 'remove', destructive: true,
  }],
}];
</script>

<template>
  <div class="row q-col-gutter-md">
    <div class="col-12">
      <hr-list>
        <hr-list-section-header>
          {{ i18n.t('staff.qualifications.titleDegrees') }}
        </hr-list-section-header>

        <hr-list-item
          v-for="row in degreeRows"
          :key="row.key"
          segmented
          clickable
          :disable="props.readonly"
          :label="row.label"
          :caption="row.caption"
          data-e2e="list-item-staff-degree"
          @click="openDegree(row.degree)"
        >
          <template #trailing>
            <hr-overflow-menu-btn
              :options="degreeMenu()"
              :disable="props.readonly"
              @click.stop
              @select="removeDegree(row.degree)"
            />
          </template>
        </hr-list-item>

        <hr-list-add-btn
          v-if="!props.readonly"
          :variant="degreeRows.length === 0 ? 'outlined' : 'flat'"
          :label="i18n.t('staff.qualifications.actionAddDegree')"
          data-e2e="btn-staff-add-degree"
          @click="openDegree()"
        />
      </hr-list>
    </div>

    <div class="col-12">
      <hr-list>
        <hr-list-section-header>
          {{ i18n.t('staff.qualifications.titleCredentials') }}
        </hr-list-section-header>

        <credential-list
          :credentials="groups.current"
          :disable="props.readonly"
          @edit="openCredential"
          @renew="openRenewal"
          @remove="removeCredential"
        />

        <hr-list-add-btn
          v-if="!props.readonly"
          :variant="groups.current.length === 0 ? 'outlined' : 'flat'"
          :label="i18n.t('staff.qualifications.actionAddCredential')"
          data-e2e="btn-staff-add-credential"
          @click="openCredential()"
        />

        <!-- Only when something is in it, so a clinic that has never had one never meets an empty
             control. Kept rather than deleted: a date of service inside an old window still
             resolves through it. -->
        <template v-if="groups.ended.length > 0">
          <div class="q-px-md q-py-sm">
            <hr-status-action-chip
              v-model="showEnded"
              chevron
              :label="i18n.t('staff.qualifications.actionShowExpired', { count: groups.ended.length })"
              data-e2e="chip-staff-show-expired"
            />
          </div>

          <credential-list
            v-if="showEnded"
            :credentials="groups.ended"
            muted
            :disable="props.readonly"
            @edit="openCredential"
            @renew="openRenewal"
            @remove="removeCredential"
          />
        </template>
      </hr-list>
    </div>

    <edit-credential-dialog
      ref="credentialDialog"
      :existing="qualifications.credentials"
      :credential="editing"
      :default-jurisdiction="props.person?.state ?? null"
      @ok="onCredentialSaved"
    />

    <edit-degree-dialog
      ref="degreeDialog"
      @ok="onDegreeSaved"
    />
  </div>
</template>
